import { useEffect, useRef, useState } from "react";
import { AlertTriangle, FileDown } from "lucide-react";
import { MarginCell, money } from "@/components/billing/RateTable";
import { MoveCycleForm, completedLabel } from "@/components/billing/MoveCycleForm";
import { api, formatApiError } from "@/lib/api";
import { todayET } from "@/lib/dates";

const STATUS_CLASS = { "Ready to bill": "compliance-result-pending", Invoiced: "compliance-result-clear", Paid: "compliance-result-cleared", Owed: "compliance-result-pending" };
const SOURCE_LABEL = { "provider rate card": "card", "client rate default": "default 70%", "entered on record": "entered" };

export const BillingBadge = ({ value, testId }) => <em className={`compliance-result ${STATUS_CLASS[value] || ""}`} data-testid={testId}>{value || "Not ready"}</em>;

export const NoRateBadge = ({ testId, label = "No rate set" }) => <em className="compliance-result compliance-result-possible no-rate-badge" data-testid={testId}><AlertTriangle size={12} /> {label}</em>;

const isUnpriced = (line) => line.billing.client_fee === null || line.billing.client_fee === undefined;

function InvoiceForm({ line, onDone, onError }) {
  const unpriced = isUnpriced(line);
  const [form, setForm] = useState({ invoice_number: line.billing.invoice_number || "", invoice_date: line.billing.invoice_date || todayET(), client_fee: "" });
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      const payload = { billing_status: "Invoiced", invoice_number: form.invoice_number, invoice_date: form.invoice_date };
      if (unpriced) payload.client_fee = Number(form.client_fee);
      await api.put(`/evaluation-billing/${line.evaluation_id}/billing`, payload);
      onDone("Invoice recorded.");
    } catch (requestError) {
      onError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="billing-inline-form">
      {unpriced && <input type="number" min="0" step="0.01" placeholder="Client fee" value={form.client_fee} data-testid={`invoice-fee-${line.evaluation_id}`} onChange={(e) => setForm({ ...form, client_fee: e.target.value })} />}
      <input placeholder="Invoice #" value={form.invoice_number} data-testid={`invoice-number-${line.evaluation_id}`} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
      <input type="date" value={form.invoice_date} data-testid={`invoice-date-${line.evaluation_id}`} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
      <button type="button" className="button button-secondary" disabled={busy || !form.invoice_number || (unpriced && form.client_fee === "")} data-testid={`mark-invoiced-${line.evaluation_id}`} onClick={save}>Mark invoiced</button>
    </span>
  );
}

function RateCell({ line, items, onChanged, onError }) {
  const locked = line.billing.billing_status !== "Ready to bill";
  async function choose(rateItemId) {
    if (!rateItemId) return;
    try {
      await api.put(`/evaluation-billing/${line.evaluation_id}/rate`, { rate_item_id: rateItemId });
      onChanged("Rate applied.");
    } catch (requestError) {
      onError(formatApiError(requestError));
    }
  }
  if (!items.length) return <NoRateBadge testId={`billing-no-rate-${line.evaluation_id}`} />;
  const needsChoice = !line.billing.rate_item_id && isUnpriced(line);
  return (
    <span className="billing-rate-cell">
      {needsChoice && <NoRateBadge label="Choose rate" testId={`billing-choose-rate-${line.evaluation_id}`} />}
      <select value={line.billing.rate_item_id || ""} disabled={locked} data-testid={`billing-rate-select-${line.evaluation_id}`} onChange={(e) => choose(e.target.value)}>
        <option value="">{needsChoice ? "Choose rate…" : "Rate…"}</option>
        {items.map((item) => <option key={item.id} value={item.id}>{item.name} · {money(item.client_fee)}</option>)}
      </select>
    </span>
  );
}

function BillingLine({ line, items, month, highlight, canMove, onChanged, onError, onMarkPaid }) {
  const [moving, setMoving] = useState(false);
  const ref = useRef(null);
  const highlighted = highlight === line.evaluation_id;
  useEffect(() => {
    if (highlighted && ref.current) ref.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlighted]);
  const unpriced = isUnpriced(line);
  const { day, basis } = completedLabel(line);
  const moved = line.billing.cycle_moved;
  return (
    <tr ref={ref} className={`${unpriced ? "is-unpriced" : ""} ${line.billing.low_margin ? "is-low-margin" : ""} ${highlighted ? "is-highlighted" : ""}`} data-testid={`billing-line-${line.evaluation_id}`}>
      <td>{line.student_initials}</td>
      <td>{line.evaluation_type}</td>
      <td>{line.provider_name || "—"}</td>
      <td data-testid={`billing-completed-${line.evaluation_id}`}>{day}{basis ? <small className="fee-source"> · {basis}</small> : null}{moved ? <small className="fee-source" title={moved.note}> · from {moved.from}</small> : null}</td>
      <td><RateCell line={line} items={items} onChanged={onChanged} onError={onError} /></td>
      <td data-testid={`billing-client-fee-${line.evaluation_id}`}>{unpriced ? "—" : money(line.billing.client_fee)}</td>
      <td data-testid={`billing-provider-fee-${line.evaluation_id}`}>{line.billing.provider_fee === null || line.billing.provider_fee === undefined ? "—" : <>{money(line.billing.provider_fee)} {line.billing.provider_fee_source && <small className="fee-source">{SOURCE_LABEL[line.billing.provider_fee_source] || line.billing.provider_fee_source}{line.billing.provider_fee_source === "provider rate card" && line.billing.provider_rate_label ? ` · ${line.billing.provider_rate_label}` : ""}</small>}</>}</td>
      <td><MarginCell clientFee={line.billing.client_fee} providerFee={line.billing.provider_fee} testId={`billing-margin-${line.evaluation_id}`} /></td>
      <td><BillingBadge value={line.billing.billing_status} testId={`billing-status-${line.evaluation_id}`} /></td>
      <td><small>{line.billing.invoice_number ? `#${line.billing.invoice_number} · ${line.billing.invoice_date}` : "—"}{line.billing.client_paid_on ? ` · paid ${line.billing.client_paid_on}` : ""}</small></td>
      <td className="credential-actions">
        {moving
          ? <MoveCycleForm line={line} defaultMonth={month} onDone={(message) => { setMoving(false); onChanged(message); }} onError={onError} onCancel={() => setMoving(false)} />
          : (
            <>
              {line.billing.billing_status === "Ready to bill" && <InvoiceForm line={line} onDone={onChanged} onError={onError} />}
              {line.billing.billing_status === "Invoiced" && <button type="button" className="button button-secondary" data-testid={`mark-client-paid-${line.evaluation_id}`} onClick={() => onMarkPaid(line)}>Mark paid</button>}
              {canMove && line.billing.billing_status === "Ready to bill" && <button type="button" className="link-button" data-testid={`move-cycle-${line.evaluation_id}`} onClick={() => setMoving(true)}>Move cycle</button>}
            </>
          )}
      </td>
    </tr>
  );
}

function ClientBlock({ client, month, rateItems, highlight, canMove, onChanged, onError }) {
  const [downloading, setDownloading] = useState(false);
  const items = rateItems.filter((item) => item.client_id === client.client_id);
  async function markPaid(line) {
    try {
      await api.put(`/evaluation-billing/${line.evaluation_id}/billing`, { billing_status: "Paid", client_paid_on: todayET() });
      onChanged("Client payment recorded.");
    } catch (requestError) {
      onError(formatApiError(requestError));
    }
  }
  async function downloadInvoice() {
    setDownloading(true);
    try {
      const response = await api.get("/evaluation-billing/invoice-pdf", { params: { month, client_id: client.client_id }, responseType: "blob" });
      const objectUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `invoice-draft-${(client.client_name || "client").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${month}.pdf`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      onError(formatApiError(requestError));
    } finally {
      setDownloading(false);
    }
  }
  return (
    <div className="billing-client-block" data-testid={`billing-client-${client.client_id}`}>
      <div className="billing-client-heading">
        <h4>{client.client_name} <small>{client.lines.length} evaluation(s) · {money(client.total)}</small>{client.unpriced ? <span className="billing-unpriced-flag" data-testid={`billing-client-unpriced-${client.client_id}`}><AlertTriangle size={13} /> {client.unpriced} without a rate — excluded from the total</span> : null}</h4>
        <button type="button" className="button button-secondary" disabled={downloading} data-testid={`invoice-pdf-${client.client_id}`} onClick={downloadInvoice}><FileDown size={14} /> {downloading ? "Preparing…" : "Invoice PDF"}</button>
      </div>
      <div className="credential-table-wrap">
        <table>
          <thead><tr><th>Student</th><th>Type</th><th>Provider</th><th>Completed</th><th>Rate</th><th>Client fee</th><th>Provider fee</th><th>Margin</th><th>Billing status</th><th>Invoice</th><th /></tr></thead>
          <tbody>
            {client.lines.map((line) => <BillingLine key={line.evaluation_id} line={line} items={items} month={month} highlight={highlight} canMove={canMove} onChanged={onChanged} onError={onError} onMarkPaid={markPaid} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const CycleBilling = ({ overview, month, rateItems = [], highlight, canMove = false, onChanged, onError, onGoToRates }) => (
  <section className="compliance-card" data-testid="cycle-billing">
    <div className="section-heading">
      <div>
        <h3>Evaluations to bill — {overview.cycle_label}</h3>
        <p>Submitted by {overview.submit_by} · bill clients by {overview.bill_by} · pay providers on {overview.pay_on}. {overview.evaluations} completed evaluation(s), {money(overview.client_total)} to bill{overview.low_margin ? ` · ${overview.low_margin} under 30% margin` : ""}.</p>
      </div>
    </div>
    {overview.unpriced > 0 && (
      <p className="form-message warning-message billing-unpriced-banner" data-testid="billing-unpriced-banner">
        <AlertTriangle size={15} /> {overview.unpriced} completed evaluation(s) have no rate for their client and type (or need one chosen). They are flagged below, excluded from totals, and cannot be invoiced or paid until a rate is set.
        <button type="button" className="link-button" data-testid="billing-go-to-rates" onClick={onGoToRates}>Open the Rate table</button>
      </p>
    )}
    {overview.by_client.map((client) => <ClientBlock key={client.client_id} client={client} month={month} rateItems={rateItems} highlight={highlight} canMove={canMove} onChanged={onChanged} onError={onError} />)}
    {!overview.by_client.length && <p className="operations-empty-state" data-testid="cycle-billing-empty">No completed evaluations in this cycle.</p>}
  </section>
);
